// src/pages/SearchableDropdown.js
import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  TextField,
  MenuItem,
  FormControl,
  InputAdornment,
  CircularProgress,
  Box,
  Typography,
  Paper,
  Popper,
  ClickAwayListener,
  Grow,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { styled } from "@mui/material/styles";

const StyledPopper = styled(Popper)(({ theme }) => ({
  zIndex: 9999,
  width: "100%",
  marginTop: "4px",
  position: "relative",
  "& .MuiPaper-root": {
    maxHeight: "300px",
    overflowY: "auto",
    borderRadius: "8px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
    border: "1px solid #e2e8f0",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#c1c7cd",
      borderRadius: "4px",
      "&:hover": {
        background: "#a0a7ae",
      },
    },
  },
}));

const SearchableDropdown = ({
  id,
  name,
  value,
  onChange,
  options = [],
  label,
  placeholder = "Search...",
  required = false,
  disabled = false,
  loading = false,
  error = false,
  helperText = "",
  fullWidth = true,
  size = "small",
  getOptionLabel = (option) => {
    if (typeof option === "string") return option;
    if (option?.label) return option.label;
    if (option?.name) return option.name;
    if (option?.displayName) return option.displayName;
    return JSON.stringify(option);
  },
  getOptionValue = (option) => {
    if (typeof option === "string") return option;
    return option.id || option.value || option.key;
  },
  filterOptions = (option, searchTerm) => {
    const label = getOptionLabel(option).toLowerCase();
    const search = searchTerm.toLowerCase().trim();
    return label.includes(search);
  },
  renderOption = (option, { selected }) => {
    const label = getOptionLabel(option);
    return (
      <MenuItem
        key={getOptionValue(option)}
        value={getOptionValue(option)}
        selected={selected}
        sx={{
          fontSize: "0.875rem",
          py: 1,
          "&.Mui-selected": {
            backgroundColor: "rgba(59, 130, 246, 0.08)",
            "&:hover": {
              backgroundColor: "rgba(59, 130, 246, 0.12)",
            },
          },
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        {label}
      </MenuItem>
    );
  },
  noOptionsText = "No options found",
  loadingText = "Loading...",
  onSearchChange,
  ...props
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const anchorRef = useRef(null);
  const inputRef = useRef(null);

  // Reset search when dropdown closes or value changes
  useEffect(() => {
    if (!open) {
      setSearchTerm("");
    }
  }, [open]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    return options.filter((option) => filterOptions(option, searchTerm));
  }, [options, searchTerm, filterOptions]);

  // Get selected option label
  const selectedOption = useMemo(() => {
    if (!value) return "";
    return options.find((opt) => getOptionValue(opt) === value);
  }, [options, value, getOptionValue]);

  const displayValue = useMemo(() => {
    if (selectedOption) {
      return getOptionLabel(selectedOption);
    }
    return "";
  }, [selectedOption, getOptionLabel]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setOpen(true);
    if (onSearchChange) {
      onSearchChange(newValue);
    }
  };

  const handleSelect = (selectedValue) => {
    if (onChange) {
      const syntheticEvent = {
        target: {
          name: name,
          value: selectedValue,
          id: id,
        },
      };
      onChange(syntheticEvent);
    }
    setOpen(false);
    setSearchTerm("");
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleFocus = () => {
    setFocused(true);
    setOpen(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  const handleClickAway = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
    setSearchTerm("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      setSearchTerm("");
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
    if (e.key === "Enter" && filteredOptions.length === 1 && open) {
      handleSelect(getOptionValue(filteredOptions[0]));
    }
  };

  return (
    <FormControl
      fullWidth={fullWidth}
      size={size}
      error={error}
      disabled={disabled}
      ref={anchorRef}
      onKeyDown={handleKeyDown}
      sx={{ position: "relative" }}  // ← ADD THIS
    >
      <TextField
        id={id}
        ref={inputRef}
        value={open ? searchTerm : displayValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={open ? placeholder : displayValue || placeholder}
        label={label}
        required={required}
        disabled={disabled || loading}
        error={error}
        helperText={helperText}
        fullWidth={fullWidth}
        size={size}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "#9ca3af", fontSize: "1rem" }} />
            </InputAdornment>
          ),
          endAdornment: loading && (
            <InputAdornment position="end">
              <CircularProgress size={16} />
            </InputAdornment>
          ),
          sx: {
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: error ? "#dc2626" : "#d1d5db",
              },
              "&:hover fieldset": {
                borderColor: error ? "#dc2626" : "#3b82f6",
              },
              "&.Mui-focused fieldset": {
                borderColor: error ? "#dc2626" : "#3b82f6",
                boxShadow: error
                  ? "0 0 0 3px rgba(220,38,38,0.12)"
                  : "0 0 0 3px rgba(59,130,246,0.12)",
              },
            },
            "& .MuiInputBase-input.Mui-disabled": {
              backgroundColor: "#f3f4f6",
              color: "#6b7280",
              cursor: "not-allowed",
            },
          },
        }}
        {...props}
      />

      <StyledPopper
        open={open && !disabled}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 4],
            },
          },
          {
            name: "preventOverflow",
            options: {
              boundary: "viewport",
              padding: 8,
            },
          },
          {
            name: "sameWidth",
            enabled: true,
            phase: "beforeWrite",
            requires: ["computeStyles"],
            fn: ({ state }) => {
              state.styles.popper.width = `${state.rects.reference.width}px`;
            },
            effect: ({ state }) => {
              state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClickAway}>
                <List dense sx={{ py: 0 }}>
                  {loading ? (
                    <ListItem sx={{ justifyContent: "center", py: 2 }}>
                      <CircularProgress size={24} />
                    </ListItem>
                  ) : filteredOptions.length === 0 ? (
                    <ListItem sx={{ justifyContent: "center", py: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "#6b7280", fontStyle: "italic" }}
                      >
                        {searchTerm.trim() ? noOptionsText : "Type to search..."}
                      </Typography>
                    </ListItem>
                  ) : (
                    filteredOptions.map((option) => {
                      const optionValue = getOptionValue(option);
                      const isSelected = value === optionValue;
                      return (
                        <ListItem
                          key={optionValue}
                          onClick={() => handleSelect(optionValue)}
                          sx={{
                            cursor: "pointer",
                            py: 0.75,
                            px: 2,
                            backgroundColor: isSelected
                              ? "rgba(59, 130, 246, 0.08)"
                              : "transparent",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          <ListItemText
                            primary={getOptionLabel(option)}
                            primaryTypographyProps={{
                              fontSize: "0.875rem",
                              color: isSelected ? "#1e40af" : "#1f2937",
                              fontWeight: isSelected ? 600 : 400,
                            }}
                          />
                        </ListItem>
                      );
                    })
                  )}
                </List>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </StyledPopper>
    </FormControl>
  );
};

export default SearchableDropdown;